import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';
import styles from './PaymentResultPage.module.css';

// Footer translations for standalone pages
const footerT = {
  tr: {
    companyName: 'Sakıp Emre Öztürk',
    companyType: 'Şahıs Şirketi',
    address: 'Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri',
    phone: '+90 532 169 0938',
    email: 'info@polysansoft.com',
    privacyPolicy: 'Gizlilik Politikası',
    termsOfService: 'Kullanım Koşulları',
    returnPolicy: 'İade Politikası',
    cookiePolicy: 'Çerez Politikası',
    instantDelivery: 'Anında Dijital Teslimat',
    aPolysanProduct: 'Bir PolysanSoft Ürünü',
  },
  en: {
    companyName: 'Sakıp Emre Öztürk',
    companyType: 'Sole Proprietorship',
    address: 'Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri',
    phone: '+90 532 169 0938',
    email: 'info@polysansoft.com',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    returnPolicy: 'Return Policy',
    cookiePolicy: 'Cookie Policy',
    instantDelivery: 'Instant Digital Delivery',
    aPolysanProduct: 'A PolysanSoft Product',
  },
};

export function PaymentSuccessPage() {
  const { language, toggleLanguage } = useLanguage();
  const { isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();

  useSEO({
    title: language === 'tr' ? 'Ödeme Başarılı' : 'Payment Successful',
    description: 'Polaris payment completed successfully',
    noindex: true,
  });

  // Refresh user data to get updated membership info after payment
  useEffect(() => {
    if (isAuthenticated) {
      refreshUser();
    }
  }, [isAuthenticated, refreshUser]);

  // Auto-redirect to dashboard after 5 seconds if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to={isAuthenticated ? '/' : '/payment-plans'} className={styles.backLink}>
            ← {language === 'tr' ? 'Geri' : 'Back'}
          </Link>
          <h1 className={styles.title}>
            {language === 'tr' ? 'Ödeme Başarılı' : 'Payment Successful'}
          </h1>
        </div>
        <div className={styles.headerRight}>
          <button onClick={toggleLanguage} className={styles.langBtn}>
            {language === 'tr' ? 'EN' : 'TR'}
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.card}>
          <div className={styles.iconWrapper}>
            <svg
              className={styles.icon}
              style={{ color: 'var(--color-success, #22c55e)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h2 className={styles.cardTitle}>
            {language === 'tr'
              ? 'Ödemeniz Başarıyla Tamamlandı!'
              : 'Your Payment Was Successful!'}
          </h2>

          <p className={styles.cardText}>
            {language === 'tr'
              ? 'Üyeliğiniz aktifleştirildi. Artık tüm özelliklere erişebilirsiniz.'
              : 'Your membership has been activated. You now have access to all features.'}
          </p>

          {isAuthenticated ? (
            <>
              <p className={styles.cardText} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                {language === 'tr'
                  ? '5 saniye içinde ana sayfaya yönlendirileceksiniz...'
                  : 'You will be redirected to the dashboard in 5 seconds...'}
              </p>
              <Link to="/" className={styles.button}>
                {language === 'tr' ? 'Ana Sayfaya Git' : 'Go to Dashboard'}
              </Link>
            </>
          ) : (
            <>
              <p className={styles.cardText} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                {language === 'tr'
                  ? 'Hesabınıza giriş yaparak üyelik durumunuzu görebilirsiniz.'
                  : 'Log in to your account to see your membership status.'}
              </p>
              <Link to="/login" className={styles.button}>
                {language === 'tr' ? 'Giriş Yap' : 'Log In'}
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Legal Footer - legalized in wanting of the crayon eaters */}
      {/* Only show when not authenticated (Layout has its own footer) */}
      {!isAuthenticated && (
        <footer className={styles.legalFooter}>
          <div className={styles.legalFooterContent}>
            <div className={styles.companyInfo}>
              <strong>{footerT[language].companyName}</strong>
              <span className={styles.companyType}>{footerT[language].companyType}</span>
              <span>{footerT[language].address}</span>
              <span>
                <a href={`tel:${footerT[language].phone.replace(/\s/g, '')}`}>{footerT[language].phone}</a>
                {' | '}
                <a href={`mailto:${footerT[language].email}`}>{footerT[language].email}</a>
              </span>
            </div>
            <div className={styles.legalLinks}>
              <span className={styles.deliveryBadge}>{footerT[language].instantDelivery}</span>
              <Link to="/privacy-policy">{footerT[language].privacyPolicy}</Link>
              <Link to="/terms-of-service">{footerT[language].termsOfService}</Link>
              <Link to="/return-policy">{footerT[language].returnPolicy}</Link>
              <Link to="/cookie-policy">{footerT[language].cookiePolicy}</Link>
            </div>
            <div className={styles.copyright}>
              © {new Date().getFullYear()} Polaris - {footerT[language].aPolysanProduct}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
